import { useState } from "react";
import React from "react";
import { CalendarPane } from "./CalendarPane";
import { I_Building, I_Cal, I_Check, I_Plus, I_Trash, I_X } from "./icons";
import { Ava } from "./ui";
import { btnP, glass, heading, monoFont } from "../styles.js";
import { sb } from "../supabase.js";

const VisitsView = ({
    visits,
    clients,
    properties,
    notes,
    onAddVisit,
    onAddNote,
    onDeleteNote,
    onOpenClient,
    onOpenProperty,
    onChanged,
    session,
  }) => {
    const [mode, setMode] = useState("calendar");
    const now = Date.now();
    const upcoming = visits
      .filter((v) => new Date(v.visited_at).getTime() >= now)
      .sort((a, b) => new Date(a.visited_at) - new Date(b.visited_at));
    const past = visits
      .filter((v) => new Date(v.visited_at).getTime() < now)
      .sort((a, b) => new Date(b.visited_at) - new Date(a.visited_at));
    const list = mode === "upcoming" ? upcoming : past;
    const cById = Object.fromEntries(clients.map((c) => [c.id, c]));
    const pById = Object.fromEntries(properties.map((p) => [p.id, p]));
    const markInterested = async (v, value) => {
      await sb
        .from("visits")
        .update({
          interested: value,
        })
        .eq("id", v.id);
      onChanged && onChanged();
    };
    const del = async (v) => {
      if (!confirm("Delete this visit?")) return;
      await sb.from("visits").delete().eq("id", v.id);
      onChanged && onChanged();
    };
    return (
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl text-slate-100 mb-1" style={heading}>
              Visits
            </h1>
            <p className="text-sm text-slate-400">Calendar of client–property meetings.</p>
          </div>
          <button onClick={() => onAddVisit(null)} className={btnP}>
            <I_Plus size={14} />
            Log a visit
          </button>
        </div>
        <div className="flex gap-1 bg-white/[0.04] rounded-lg p-1 border border-white/[0.06] w-fit">
          {[
            ["calendar", "Calendar"],
            ["upcoming", `Upcoming · ${upcoming.length}`],
            ["past", `Past · ${past.length}`],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setMode(k)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${mode === k ? "bg-white/[0.08] text-slate-100" : "text-slate-400 hover:text-slate-200"}`}
            >
              {l}
            </button>
          ))}
        </div>
        {mode === "calendar" ? (
          <CalendarPane
            visits={visits}
            notes={notes || []}
            clients={clients}
            properties={properties}
            session={session}
            onOpenClient={onOpenClient}
            onOpenProperty={onOpenProperty}
            onAddNote={onAddNote}
            onDeleteNote={onDeleteNote}
            onChanged={onChanged}
          />
        ) : list.length === 0 ? (
          <div className={`${glass} rounded-2xl p-12 text-center`}>
            <I_Cal size={32} className="mx-auto mb-2 text-slate-600" />
            <div className="text-sm text-slate-400">
              {mode === "upcoming" ? "No upcoming visits scheduled." : "No past visits logged yet."}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((v) => {
              const c = cById[v.client_id];
              const p = pById[v.property_id];
              const d = new Date(v.visited_at);
              return (
                <div key={v.id} className={`${glass} rounded-xl p-4 flex items-center gap-4`}>
                  <div className="w-14 text-center shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-amber-300">
                      {d.toLocaleDateString("en-IN", {
                        month: "short",
                      })}
                    </div>
                    <div className="text-2xl text-slate-100" style={heading}>
                      {d.getDate()}
                    </div>
                    <div className="text-[10px] text-slate-500" style={monoFont}>
                      {d.toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <button
                      onClick={() => c && onOpenClient(c.id)}
                      className="text-left flex items-center gap-2 min-w-0"
                    >
                      <Ava name={c?.name || v.walkin_name || "?"} photo={c?.photo} size="sm" />
                      <div className="min-w-0">
                        <div className="text-sm text-slate-100 truncate">
                          {c?.name || v.walkin_name || "Walk-in"}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate">Client</div>
                      </div>
                    </button>
                    <button
                      onClick={() => p && onOpenProperty(p.id)}
                      className="text-left flex items-center gap-2 min-w-0"
                    >
                      <div className="w-9 h-9 rounded-lg bg-white/[0.04] overflow-hidden flex items-center justify-center shrink-0">
                        {p?.cover_photo ? (
                          <img src={p.cover_photo} className="w-full h-full object-cover" />
                        ) : (
                          <I_Building size={14} className="text-slate-500" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm text-slate-100 truncate">{p?.title || "—"}</div>
                        <div className="text-[11px] text-slate-400 truncate">
                          {p?.locality || "Property"}
                        </div>
                      </div>
                    </button>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    {mode === "past" && (
                      <React.Fragment>
                        <button
                          onClick={() => markInterested(v, true)}
                          title="Mark interested"
                          className={`p-1.5 rounded ${v.interested === true ? "bg-emerald-500/20 text-emerald-200" : "hover:bg-white/[0.06] text-slate-500"}`}
                        >
                          <I_Check size={13} />
                        </button>
                        <button
                          onClick={() => markInterested(v, false)}
                          title="Mark not interested"
                          className={`p-1.5 rounded ${v.interested === false ? "bg-stone-500/20 text-stone-300" : "hover:bg-white/[0.06] text-slate-500"}`}
                        >
                          <I_X size={13} />
                        </button>
                      </React.Fragment>
                    )}
                    <button
                      onClick={() => del(v)}
                      className="p-1.5 rounded hover:bg-white/[0.06] text-slate-500 hover:text-rose-300"
                    >
                      <I_Trash size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

export { VisitsView };
